import Component from '@glimmer/component';
import type { TextWeight } from './text';

/**
 * Heading level — determines the HTML element (h1–h6) for semantics/a11y.
 */
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Visual size — controls font size independently of semantic level.
 * When omitted, auto-maps from level.
 */
export type HeadingSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';

/** Visual typography roles shared by page, section, and panel headings. */
export type HeadingRole = 'page-title' | 'section-title' | 'panel-title';

interface HeadingSignature {
  Args: {
    /** Semantic heading level (h1–h6). Default: 2 */
    level?: HeadingLevel;
    /** Visual role independent of document level. Ignored when `@size` is supplied. */
    role?: HeadingRole;
    /** Compatibility size override. Takes precedence over `@role` and the level mapping. */
    size?: HeadingSize;
    /**
     * Compatibility font-weight override applied over the role/size/level
     * default (always semibold today). The only supported way to get a
     * heavier or lighter heading — never reach for a raw `font-*` utility at
     * the call site, which only coincidentally out-cascades the default.
     */
    weight?: TextWeight;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLHeadingElement;
}

const LEVEL_TO_ROLE: Partial<Record<HeadingLevel, HeadingRole>> = {
  1: 'page-title',
  2: 'section-title',
  3: 'panel-title',
};

const LEVEL_TO_FALLBACK_SIZE: Record<HeadingLevel, HeadingSize> = {
  1: '2xl',
  2: 'lg',
  3: 'base',
  4: 'sm',
  5: 'xs',
  6: 'xs',
};

const SIZE_CLASSES: Record<HeadingSize, string> = {
  '3xl': 'tio-type-size-3xl',
  '2xl': 'tio-type-size-2xl',
  xl: 'tio-type-size-xl',
  lg: 'tio-type-size-lg',
  base: 'tio-type-size-base',
  sm: 'tio-type-size-sm',
  xs: 'tio-type-size-xs',
};

const ROLE_CLASSES: Record<HeadingRole, string> = {
  'page-title': 'tio-type-page-title',
  'section-title': 'tio-type-section-title',
  'panel-title': 'tio-type-panel-title',
};

const WEIGHT_CLASSES: Record<TextWeight, string> = {
  normal: 'tio-type-weight-normal',
  medium: 'tio-type-weight-medium',
  semibold: 'tio-type-weight-semibold',
  bold: 'tio-type-weight-bold',
};

/**
 * level controls the semantic h1–h6 element; role or compatibility size can override its visual
 * treatment.
 */
export default class Heading extends Component<HeadingSignature> {
  get level() {
    return this.args.level ?? 2;
  }

  get sizeClass() {
    if (this.args.size) {
      return SIZE_CLASSES[this.args.size];
    }

    const role = this.args.role ?? LEVEL_TO_ROLE[this.level];
    return role ? ROLE_CLASSES[role] : SIZE_CLASSES[LEVEL_TO_FALLBACK_SIZE[this.level]];
  }

  get weightClass() {
    return this.args.weight ? WEIGHT_CLASSES[this.args.weight] : '';
  }

  get tag() {
    return `h${this.level}` as const;
  }

  <template>
    {{#let (element this.tag) as |Tag|}}
      <Tag
        data-test-heading
        class="app-heading {{this.sizeClass}} {{this.weightClass}}"
        ...attributes
      >{{yield}}</Tag>
    {{/let}}
  </template>
}

export type { HeadingSignature };
